import React, { FC, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import {
  getLanguageKey,
  LocalesLanguagesConstants,
} from '../../../locales/locales';
import { LinkUpdateRequestDto } from '../../../store/landscapeAPI/link/dto/request/link-update-request.dto';
import { LinkResponseDto } from '../../../store/landscapeAPI/link/dto/response/link-response.dto';
import {
  fetchLinkById,
  updateLink,
} from '../../../store/landscapeAPI/link/reducers/link.slice';
import { AppDispatch, RootState } from '../../../store/store';
import {
  translateObject,
  TranslateParams,
} from '../../../store/translateAPI/translate/reducers/translate.slice';
import { showSuccess } from '../../../utils/Notifications';
import Spinner from '../../atoms/Spinner/Spinner';
import { LandscapeIndicatorExternalResourceEditTemplate } from '../../templates';

// import styles from './LandscapeIndicatorExternalResourceEditPage.module.css';

interface LandscapeIndicatorExternalResourceEditPageProps {}

const LandscapeIndicatorExternalResourceEditPage: FC<
  LandscapeIndicatorExternalResourceEditPageProps
> = () => {
  const { id, landscapeIndicatorId } = useParams();

  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();

  const [externalResource, setExternalResource] = useState<LinkResponseDto>();

  const { link: selector, linkStatus: status } = useSelector(
    (state: RootState) => state.linkSlice
  );

  const { status: translateStatus } = useSelector(
    (state: RootState) => state.translateSlice
  );

  const { externalResourceId } = useParams();

  useEffect(() => {
    if (status === 'succeeded' && selector) {
      setExternalResource(selector);
    }
  }, [selector, status]);

  const [selectedLanguage, setLanguage] = useState<LocalesLanguagesConstants>(
    LocalesLanguagesConstants.en
  );

  const [fromLanguage, setFromLanguage] = useState<LocalesLanguagesConstants>(
    LocalesLanguagesConstants.en
  );

  const [toLanguage, setToLanguage] = useState<LocalesLanguagesConstants>(
    LocalesLanguagesConstants.en
  );

  const translate = async (toLanguage: LocalesLanguagesConstants) => {
    const params: TranslateParams<LinkResponseDto> = {
      obj: externalResource!,
      from: getLanguageKey(fromLanguage)!,
      to: [getLanguageKey(toLanguage)!],
    };
    const response = await dispatch(translateObject(params)).unwrap();
    const request: LinkResponseDto = {
      ...(response as LinkResponseDto),
    };
    if (externalResource?.compactId) {
      await dispatch(
        updateLink({
          updateRequest: {
            ...externalResource,
            title: request.title ?? '',
            description: request.description ?? '',
          },
          language: toLanguage,
        })
      ).unwrap();
      setLanguage(toLanguage);
    }
  };

  const generateAllTranslations = async () => {
    return await Promise.allSettled(
      Object.values(LocalesLanguagesConstants).map(translate)
    );
  };

  const generateTranslation = async () => {
    return await translate(toLanguage);
  };

  useEffect(() => {
    if (externalResourceId) {
      dispatch(
        fetchLinkById({ id: externalResourceId, language: selectedLanguage })
      );
    }
  }, [dispatch, externalResourceId, selectedLanguage]);

  const handleEditExternalResource = async (
    externalResource: LinkUpdateRequestDto
  ) => {
    await dispatch(
      updateLink({
        updateRequest: externalResource,
        language: toLanguage,
      })
    );

    showSuccess('External resource saved successfully.');
    navigate(
      `/landscapes/edit/${id}/indicators/edit/${landscapeIndicatorId}/external-resources`
    );
  };

  const handleExternalResourceChange = (externalResource: LinkResponseDto) => {
    setExternalResource(externalResource);
  };

  if (
    status === 'idle' ||
    status === 'loading' ||
    translateStatus === 'loading'
  ) {
    return <Spinner />;
  }

  return (
    <>
      {externalResource && (
        <LandscapeIndicatorExternalResourceEditTemplate
          externalResource={externalResource}
          handleExternalResourceChange={handleExternalResourceChange}
          handleEditExternalResource={handleEditExternalResource}
          setLanguage={setLanguage}
          selectedLanguage={selectedLanguage}
          fromLanguage={fromLanguage}
          setFromLanguage={setFromLanguage}
          toLanguage={toLanguage}
          setToLanguage={setToLanguage}
          generateTranslation={generateTranslation}
          generateAllTranslations={generateAllTranslations}
        />
      )}
    </>
  );
};

export default LandscapeIndicatorExternalResourceEditPage;
